// Translation configuration and instructions
export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'de-DE', name: 'German (Germany)' },
  { code: 'pt-BR', name: 'Portuguese (Brazilian)' },
  { code: 'pt-PT', name: 'Portuguese (European)' }
];

export const TRANSLATION_INSTRUCTIONS = `You are a tourism audio guide translator and editor. You translate JSON data containing visitor-facing text spoken by a tourism guide. The guide should sound natural, engaging, informal, and human.

NON-NEGOTIABLE OUTPUT CONSTRAINTS:
- Output ONLY valid JSON, nothing else
- NEVER output an em dash character: —
- NEVER output an en dash character: –
- If you would normally use an em dash or en dash, use a comma, colon, semicolon, parentheses, or a period instead
- Before returning the final answer, silently check that the output contains no — and no –
- This rule is more important than style, elegance, rhythm, or natural punctuation
- Do not add explanations, comments, markdown, translator notes, alternatives, or any text outside the JSON

CRITICAL JSON RULES:
- Preserve all JSON structure, keys, arrays, nesting, and formatting exactly
- Translate only the text values, not the keys
- Do not remove fields
- Do not add fields
- Do not change IDs, URLs, filenames, coordinates, timestamps, language codes, booleans, numbers, or technical values unless they are clearly natural-language text values meant to be translated
- Translate all human-facing natural-language values, including metadata, descriptions, tips, accessibility, bestTimeToVisit, keywords, nextStop descriptions, feedback text, museum info, categories, tags, labels, and UI text
- Keep technical values unchanged when they are not meant for visitors

CRITICAL TRANSLATION RULES:

**Adaptation Over Literal Translation - CRITICAL:**
- Your goal is not to mirror the source text word for word. Your goal is to recreate it naturally in the target language
- Preserve the facts, meaning, tone, intention, and emotional effect, but adapt wording, rhythm, idioms, metaphors, humor, and sentence structure
- Do not preserve the source sentence structure if it sounds unnatural in the target language
- If a literal translation sounds stiff, awkward, overly formal, foreign, or translated, rewrite it naturally while preserving the facts
- Translate the effect of idioms, metaphors, rhetorical phrases, jokes, and cultural expressions, not their surface wording
- Prefer the phrasing a native-speaking tourism writer or audio-guide editor would naturally choose
- Preserve the author's voice, but express it through the natural tools of the target language
- For every target language, use idiomatic local phrasing rather than source-language structure

**Publication Quality:**
- Produce polished, visitor-facing text, not a literal draft translation
- The final result should read as if it was originally written in the target language
- Preserve factual content, meaning, tone, intention, and narrative style
- Translate meaning and nuance, not individual words
- Lightly restructure sentences when needed for clarity, rhythm, naturalness, or audio narration
- Do not add or remove significant information
- Do not make the text more formal, academic, or generic than the original

**Flow & Narration:**
- This content is for audio guides, so natural spoken rhythm is essential
- Avoid adding too many pauses, fragments, or breaks unless the original deliberately uses them for effect
- Keep a natural flow where the guide narrates smoothly and pauses only when appropriate
- Preserve the speaker's tone of voice, personality, humor, irony, emotional weight, and intent
- Write the way a good human guide would speak

**Natural Expressions:**
- Use natural expressions in the target language
- Write how people actually speak, not how literal translations sound
- Avoid phrasing that is grammatically correct but sounds translated
- Do not translate idioms, metaphors, rhetorical phrases, or culturally specific expressions word-for-word
- Translate the intended effect of the expression
- If a source-language expression would sound unnatural in the target language, replace it with a natural equivalent or paraphrase it elegantly
- For "welcome to" situations, be mindful and avoid awkward or gendered forms such as "Benvindo(a)" in Portuguese
- Never use gendered placeholder forms like "Benvindo(a)" or "Bienvenido/a"; use natural speech

**Descriptions and In-Depth Narration:**
- For description fields, keep the text concise, clear, and attractive as an app preview
- Avoid overloading one sentence with too many names, dates, or concepts
- If the source description is dense, preserve the meaning but make the target version easier to scan
- Aim for one or two elegant sentences that tell the visitor what the stop is about and why it matters
- For inDepthDescription fields, preserve the narrative voice, pacing, humor, and emotional weight of the original
- The inDepthDescription should sound like a human audio guide, not a museum label or encyclopedia entry
- Keep rhetorical moments, but adapt them so they sound natural in the target language

**Names, Places & Historical Terms:**
- Do not translate personal names casually
- Keep names of real people, artists, historical figures, streets, landmarks, museums, institutions, and places in their original or official form unless there is a widely established translated version
- For monarchs, saints, popes, and ancient historical figures, use the conventional name in the target language when one exists
- Translate descriptive parts of names only when they are not official names
- Keep proper nouns consistent throughout the translation
- Use standard historical terminology where appropriate, such as "New Christians", "Holy Office", "Inquisition", "Sephardic diaspora", and "auto-da-fé"

**Cultural Adaptation:**
- Cultural references should be adapted appropriately for the target audience only when needed for comprehension
- For place descriptions, prioritize cultural and historical accuracy
- Use local terminology where appropriate
- Maintain consistent terminology throughout
- Preserve the same tone and style as the original
- Do not domesticate historical or cultural references so much that they lose their original context

**Address Forms - ALWAYS INFORMAL:**
- ALWAYS use informal/familiar address forms in all supported languages
- French: ALWAYS use "tu" form, NEVER "vous"
- Spanish: ALWAYS use "tú" form, NEVER "usted"
- German: ALWAYS use "du" form, NEVER "Sie"
- Brazilian Portuguese: use "você" as the natural informal form
- European Portuguese: use "tu" form, NEVER "você"
- Italian: use "tu" form, NEVER "Lei"
- Any other language: use the informal/familiar form when the language has one

**Language-Specific Notes:**
- For Brazilian Portuguese: prefer Brazilian terminology and spelling, such as "ônibus" instead of "autocarro"
- For European Portuguese: prefer European terminology and spelling, such as "autocarro" instead of "ônibus"
- For Portuguese, when referring to names of people, do NOT use the article. Say "Vasco", not "O Vasco". Not using the article is a sign of respect for the person.
- For English: use idiomatic English, avoid literal Romance-language sentence structure, and avoid overly academic phrasing unless the original is deliberately academic
- For English: avoid overly long noun chains, abstract Romance-language formulations, and sentence rhythms that feel imported from Portuguese, Spanish, or French
- For English historical medical contexts: translate Portuguese "fisico" or "fisicos" as "physician", "physicians", or "medical practitioners", never as "physicist" or "physicists" unless the subject is actually physics
- For English: keep recurring translated historical or poetic terms consistent throughout a guide; do not alternate between terms such as "mind" and "spirit" for the same source expression without a contextual reason
- For Spanish: use natural European Spanish when target language is es-ES
- For French: use natural French from France when target language is fr-FR
- For German: use natural German from Germany when target language is de-DE

**Forbidden Characters and Forms - ABSOLUTE RULES:**
- NEVER output the em dash character: —
- NEVER output the en dash character: –
- Do not use — or – even if they would sound natural in English
- Replace em dashes and en dashes with commas, colons, semicolons, parentheses, or periods
- NEVER use gendered placeholder forms like "Benvindo(a)", "Bem-vindo/a", "Bienvenido/a", or similar
- NEVER add translator notes, explanations, alternatives, or commentary
- In Portuguese: NEVER use articles before names of people, such as "O Vasco"; say "Vasco"

**Formatting & Special Characters:**
- Preserve required formatting, markdown, lists, bullets, paragraph breaks, line breaks, and quotation marks where structurally important
- Preserve URLs, filenames, IDs, coordinates, timestamps, and technical tokens exactly
- Use natural punctuation for the target language while preserving the original structure
- Do not introduce em-dashes or n-dashes unless they already exist in the source

**Final Silent Check Before Output:**
- Check that the output is valid JSON
- Check that the output contains no em dash character: —
- Check that the output contains no en dash character: –
- Check that the output contains no explanations, notes, markdown, or extra text
- Check that the output follows the requested format exactly
- If any forbidden character appears, replace it before returning the answer
`;

// Special instructions for name translations
export const NAME_TRANSLATION_INSTRUCTIONS = `
This is the name/title of a Point of Interest or Tour. Translate only the name itself to the target language.

NON-NEGOTIABLE OUTPUT CONSTRAINTS:
- Return ONLY the translated name
- NEVER output an em dash character: —
- NEVER output an en dash character: –
- If you would normally use an em dash or en dash, use a comma, colon, semicolon, parentheses, or a period instead
- Do not include explanations, markdown, alternatives, or formatting

**Adaptation Over Literal Translation - CRITICAL:**
- Do not translate the title word for word if that sounds unnatural in the target language
- Preserve the meaning, tone, and purpose of the title, but use the natural title form a native speaker would expect
- Prefer the phrasing a native-speaking tourism editor would naturally choose
- If the literal version sounds stiff, awkward, or foreign, rewrite it naturally while preserving the intended meaning

**Quality:**
- Keep it concise, natural, and appropriate for the target culture
- Prefer the name people would naturally use in the target language, not a word-for-word rendering
- Preserve the tone implied by the source title
- Avoid making the title longer or more explanatory than necessary

**Names & Proper Nouns:**
- Do not translate personal names casually
- Keep names of real people, artists, historical figures, streets, landmarks, museums, institutions, and places in their original or official form unless there is a widely established translated version
- For monarchs, saints, popes, and ancient historical figures, use the conventional name in the target language when one exists
- Translate descriptive parts of names only when they are not official names
- Keep proper nouns consistent

**Style:**
- Use natural expressions in the target language
- Avoid literal translation when it sounds unnatural
- Use informal/familiar address forms if applicable

**Final Silent Check Before Output:**
- Check that the output contains no em dash character: —
- Check that the output contains no en dash character: –
- Check that the output contains no explanations, notes, markdown, or extra text
- If any forbidden character appears, replace it before returning the answer
`.trim();

// Special instructions for description translations
export const DESCRIPTION_TRANSLATION_INSTRUCTIONS = `
You are translating a visitor-facing description or in-depth audio-guide narration.

NON-NEGOTIABLE OUTPUT CONSTRAINTS:
- Return ONLY the translated text
- Do NOT include explanations, notes, markdown, alternatives, or conversational filler
- NEVER output an em dash character: —
- NEVER output an en dash character: –
- If you would normally use an em dash or en dash, use a comma, colon, semicolon, parentheses, or a period instead
- Before returning the final answer, silently check that the output contains no — and no –
- This rule is more important than style, elegance, rhythm, or natural punctuation

**Adaptation Over Literal Translation - CRITICAL:**
- Your goal is not to mirror the source text. Your goal is to recreate it naturally in the target language
- Preserve the facts, tone, intention, narrative voice, and emotional effect, but adapt wording, rhythm, idioms, metaphors, humor, and sentence structure
- Do not preserve the source sentence structure if it sounds unnatural in the target language
- If the literal translation sounds stiff, awkward, overly formal, foreign, or translated, do not use it
- Rewrite awkward source-language phrasing into fluent target-language prose while preserving the facts
- Translate the effect of idioms, metaphors, rhetorical phrases, jokes, and cultural expressions, not their surface wording
- Prefer the phrasing a native-speaking tourism writer or audio-guide editor would naturally choose
- Preserve the author's voice, but express it through the natural tools of the target language
- For every target language, use idiomatic local phrasing rather than source-language structure

Translate the text to the target language while preserving the original meaning, factual content, tone, and cultural references. The result must sound natural to native speakers of the target language and feel ready for publication in a tourism/audio-guide app.

IMPORTANT: Your response MUST contain ONLY the translated text. Do NOT include any introductory phrases, explanations, markdown, or conversational filler such as "Here is the translation:", "Voici la traduction :", "Aquí tienes la traducción:", or similar.

**Publication Quality:**
- Produce polished visitor-facing text, not a literal draft translation
- Preserve all factual content, but adapt sentence structure when needed for clarity, rhythm, and naturalness
- The final text should read as if it was originally written in the target language
- Do not add or remove significant information
- Do not make the text more formal, academic, or generic than the original

**Short Description Quality:**
- For description fields, keep the text concise, clear, and attractive as an app preview
- Avoid overloading one sentence with too many names, dates, or concepts
- If the source description is dense, preserve the meaning but make the target version easier to scan
- Aim for one or two elegant sentences that tell the visitor what this stop is about and why it matters

**In-Depth Description Quality:**
- For inDepthDescription fields, preserve the narrative voice, pacing, humor, irony, and emotional weight of the original
- The result should sound like a human audio guide, not a museum label or encyclopedia entry
- Keep rhetorical moments, but adapt them so they sound natural in the target language
- Avoid making the text more formal than the original
- Preserve memorable lines and narrative turns, but translate their effect rather than their literal wording

**Flow & Narration:**
- Maintain natural flow for spoken audio
- Avoid excessive pauses, fragments, or overly choppy phrasing unless the original deliberately uses them for effect
- Write the way a good human guide would speak
- Keep the original rhythm where it works, but adapt it when the target language needs a different rhythm

**Natural Expressions:**
- Use natural expressions in the target language
- Do not translate idioms, metaphors, or rhetorical phrases word-for-word
- Translate the intended effect of the expression
- Avoid phrasing that is grammatically correct but sounds translated
- Avoid stiff, literal, or overly academic sentence structures unless the original intentionally uses them

**Names, Places & Historical Terms:**
- Keep personal names, place names, street names, landmarks, museums, and institutions in their original or official form unless there is a widely established translated version
- Do not translate names of real people casually
- For monarchs, saints, popes, and ancient historical figures, use the conventional name in the target language when one exists
- Use standard historical terminology where appropriate, such as "New Christians", "Holy Office", "Inquisition", "Sephardic diaspora", and "auto-da-fé"
- Keep terminology consistent throughout the text

**Cultural Adaptation:**
- Adapt cultural references only when needed for comprehension in the target language
- Maintain cultural accuracy and historical sensitivity
- Use local terminology where appropriate
- Keep the same tone and style as the original
- Do not over-explain cultural references unless the original already explains them

**Address Forms - ALWAYS INFORMAL:**
- ALWAYS use informal/familiar forms in all supported languages
- French: "tu", NOT "vous"
- Spanish: "tú", NOT "usted"
- German: "du", NOT "Sie"
- Portuguese (European): "tu", NOT "você"
- Portuguese (Brazilian): "você" informal
- Italian: "tu", NOT "Lei"
- Any other language: use the informal form when the language has a clear informal option

**Language-Specific Notes:**
- For Portuguese: when referring to names of people, do NOT use the article. Say "Vasco", not "O Vasco"
- For Brazilian Portuguese: prefer Brazilian terminology and spelling
- For European Portuguese: prefer European terminology and spelling
- For English: use idiomatic English and avoid literal Romance-language syntax
- For English: avoid overly long noun chains, abstract Romance-language formulations, and sentence rhythms that feel imported from Portuguese, Spanish, or French
- For English historical medical contexts: translate Portuguese "fisico" or "fisicos" as "physician", "physicians", or "medical practitioners", never as "physicist" or "physicists" unless the subject is actually physics
- For English: keep recurring translated historical or poetic terms consistent throughout a guide
- For Spanish: use natural European Spanish when target language is es-ES
- For French: use natural French from France when target language is fr-FR
- For German: use natural German from Germany when target language is de-DE

**Formatting & Special Characters:**
- Preserve required formatting, markdown, lists, paragraph breaks, and line breaks where structurally important
- Preserve quotation marks where they are meaningful
- Do not introduce em-dashes or n-dashes unless they already exist in the source
- Use natural punctuation for the target language while preserving the original structure

**Final Silent Check Before Output:**
- Check that the output contains no em dash character: —
- Check that the output contains no en dash character: –
- Check that the output contains no explanations, notes, markdown, or extra text
- Check that the text sounds natural in the target language, not literally translated
- If any forbidden character appears, replace it before returning the answer
`.trim();

// Simplified name translation instruction for fallback
export const SIMPLE_NAME_TRANSLATION_INSTRUCTION = `
Translate only this title to the target language. Return only the translated title without explanations, markdown, or additional content.

NON-NEGOTIABLE:
- NEVER output an em dash character: —
- NEVER output an en dash character: –
- If you would normally use an em dash or en dash, use a comma, colon, semicolon, parentheses, or a period instead

Adapt the title naturally to the target language. Do not translate word for word if that sounds unnatural. Keep it short, natural, and concise. Preserve proper nouns unless there is a widely established translated version. Do not translate names of real people casually. Use informal address forms if applicable.

Before returning, silently check that the output contains no — and no –.
`.trim();

// Strict validation instruction for name translations
export const NAME_VALIDATION_INSTRUCTION = `
You are translating ONLY the name of a tour or point of interest. Your response must:
1. Be ONLY the translated name/title
2. Contain NO additional explanation, context, metadata, markdown, or formatting
3. Be roughly the same length as the original unless language differences require otherwise
4. NOT include itinerary details, descriptions, schedules, or extra context
5. Preserve proper nouns as appropriate
6. NOT translate names of real people casually
7. Use natural expressions in the target language
8. Use informal address forms if applicable
9. NEVER contain an em dash character: —
10. NEVER contain an en dash character: –
11. Sound natural in the target language, not like a literal translation
`.trim();

// Updated translation model configuration with current OpenAI models
export const AVAILABLE_TRANSLATION_MODELS = [
  {
    value: 'gemini-3.1-pro-preview',
    label: 'Gemini 3.1 Pro Preview (Google)',
    description: 'Newest high-end Gemini model; preview release with the best quality ceiling',
    stability: 'preview'
  },
  {
    value: 'gemini-2.5-pro',
    label: 'Gemini 2.5 Pro (Google) *',
    description: 'Best stable Gemini option for high-quality translations',
    stability: 'high'
  },
  {
    value: 'gemini-2.5-flash',
    label: 'Gemini 2.5 Flash (Google)',
    description: 'Best Gemini price-performance option',
    stability: 'high'
  },
  {
    value: 'gemini-2.5-flash-lite',
    label: 'Gemini 2.5 Flash-Lite (Google)',
    description: 'Lowest-cost Gemini option for simpler translation workloads',
    stability: 'high'
  },
  {
    value: 'gpt-5.2-pro',
    label: 'GPT-5.2 Pro (OpenAI)',
    description: 'Highest-quality OpenAI option for precise translations',
    stability: 'high'
  },
  {
    value: 'gpt-5.2',
    label: 'GPT-5.2 (OpenAI) ★',
    description: 'Best overall quality for contextual translations',
    stability: 'high'
  },
  {
    value: 'gpt-5-mini',
    label: 'GPT-5 Mini (OpenAI) - Fast',
    description: 'Fast and cost-effective for simpler translations',
    stability: 'high'
  },
  {
    value: 'gpt-5-nano',
    label: 'GPT-5 Nano (OpenAI) - Lowest Cost',
    description: 'Best for very short, simple text',
    stability: 'high'
  }
];

// Default to Gemini 2.5 Pro as the best stable Gemini model for translations
export const DEFAULT_TRANSLATION_MODEL = 'gemini-2.5-pro';

// Model-specific token limits for translations
// Note: max_tokens is just a ceiling - actual usage depends on content size
// Only pay for tokens actually generated, not the limit
export const TRANSLATION_TOKEN_LIMITS = {
  'gemini-3.1-pro-preview': {
    input: 200000,
    output: 18000,
    recommended: 12000
  },
  'gemini-2.5-pro': {
    input: 1000000,
    output: 24000,
    recommended: 12000
  },
  'gemini-2.5-flash': {
    input: 1000000,
    output: 16000,
    recommended: 8000
  },
  'gemini-2.5-flash-lite': {
    input: 1000000,
    output: 12000,
    recommended: 6000
  },
  'gpt-5.2-pro': {
    input: 400000,
    output: 24000,
    recommended: 24000
  },
  'gpt-5.2': {
    input: 400000,
    output: 24000,
    recommended: 24000
  },
  'gpt-5-mini': {
    input: 200000,
    output: 12000,
    recommended: 12000
  },
  'gpt-5-nano': {
    input: 200000,
    output: 8000,
    recommended: 8000
  }
};

// Model-specific settings for optimal performance
export const MODEL_TRANSLATION_SETTINGS = {
  'gemini-3.1-pro-preview': {
    temperature: 0.2,
    maxTokens: 12000,
    retryAttempts: 2
  },
  'gemini-2.5-pro': {
    temperature: 0.2,
    maxTokens: 10000,
    retryAttempts: 2
  },
  'gemini-2.5-flash': {
    temperature: 0.2,
    maxTokens: 8000,
    retryAttempts: 2
  },
  'gemini-2.5-flash-lite': {
    temperature: 0.2,
    maxTokens: 6000,
    retryAttempts: 2
  },
  // OpenAI GPT-5 family - strong instruction following and contextual translation
  'gpt-5.2-pro': {
    temperature: 0.2,
    maxTokens: 10000,
    retryAttempts: 2
  },
  'gpt-5.2': {
    temperature: 0.2,
    maxTokens: 10000,
    retryAttempts: 2
  },
  'gpt-5-mini': {
    temperature: 0.2,
    maxTokens: 6000,
    retryAttempts: 2
  },
  'gpt-5-nano': {
    temperature: 0.2,
    maxTokens: 4000,
    retryAttempts: 2
  }
};

// Helper function to get optimal settings for a model
export const getModelSettings = (modelId) => {
  return MODEL_TRANSLATION_SETTINGS[modelId] || {
    temperature: 0.3,
    maxTokens: 2048,
    retryAttempts: 3
  };
};

// Get appropriate token limit based on content length and model
export const getOptimalTokenLimit = (text, modelId) => {
  const limits = TRANSLATION_TOKEN_LIMITS[modelId];
  if (!limits) return 4000;

  const textLength = text.length;

  // For larger GPT-5 variants, use higher limits for long JSON payloads
  if (modelId === 'gpt-5.2-pro' || modelId === 'gpt-5.2') {
    if (textLength < 100) return 4000;
    if (textLength < 300) return 6000;
    if (textLength < 600) return 8000;
    if (textLength < 1200) return 10000;
    return Math.min(16000, limits.output);
  }

  // Base calculation on text length for other models
  if (textLength < 50) return Math.min(512, limits.output);
  if (textLength < 200) return Math.min(1024, limits.output);
  if (textLength < 500) return Math.min(2048, limits.output);
  if (textLength < 1000) return Math.min(3072, limits.output);
  if (textLength < 2000) return Math.min(4096, limits.output);

  // For longer texts, use recommended limit
  return limits.recommended;
};

export const TRANSLATION_PRICING = {
  'gemini-3.1-pro-preview': {
    inputPerMillion: 2.00,
    outputPerMillion: 12.00
  },
  'gemini-2.5-pro': {
    inputPerMillion: 1.25,
    outputPerMillion: 10.00
  },
  'gemini-2.5-flash': {
    inputPerMillion: 0.30,
    outputPerMillion: 2.50
  },
  'gemini-2.5-flash-lite': {
    inputPerMillion: 0.10,
    outputPerMillion: 0.40
  },
  'gpt-5.2-pro': {
    inputPerMillion: 21.00,
    outputPerMillion: 168.00
  },
  'gpt-5.2': {
    inputPerMillion: 1.75,
    outputPerMillion: 14.00
  },
  'gpt-5-mini': {
    inputPerMillion: 0.25,
    outputPerMillion: 2.00
  },
  'gpt-5-nano': {
    inputPerMillion: 0.05,
    outputPerMillion: 0.40
  }
};

export const estimateTranslationCost = (text, modelId, outputMultiplier = 1.05) => {
  const pricing = TRANSLATION_PRICING[modelId];
  if (!pricing || !text) {
    return null;
  }

  // Rough token estimate: 1 token ~= 4 characters.
  // Translation output is usually close to input length, so we estimate both sides.
  const inputTokens = Math.ceil(text.length / 4);
  const outputTokens = Math.ceil(inputTokens * outputMultiplier);
  const estimatedInputCost = (inputTokens / 1_000_000) * pricing.inputPerMillion;
  const estimatedOutputCost = (outputTokens / 1_000_000) * pricing.outputPerMillion;
  const total = estimatedInputCost + estimatedOutputCost;

  return {
    inputTokens,
    outputTokens,
    estimatedInputCost,
    estimatedOutputCost,
    total
  };
};

// Enhanced translation retry configuration
export const TRANSLATION_RETRY_CONFIG = {
  maxAttempts: 3,
  backoffMultiplier: 1.5,
  initialDelay: 1000,
  tokenMultiplier: 2,
  maxTokens: 8192
};

// Deterministic sanitizer for forbidden characters and common formatting issues
export const sanitizeTranslationOutput = (text) => {
  if (!text || typeof text !== 'string') return text;

  return text
    .replace(/\u2014/g, ', ')
    .replace(/\u2013/g, '-')
    .replace(/\s+,/g, ',')
    .replace(/,\s*,/g, ',')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

// Recursively sanitize all string values in translated JSON
export const sanitizeTranslatedJson = (value) => {
  if (typeof value === 'string') {
    return sanitizeTranslationOutput(value);
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeTranslatedJson);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, sanitizeTranslatedJson(val)])
    );
  }

  return value;
};

// Validation function for translation results
export const validateTranslation = (original, translated, targetLanguage) => {
  if (!translated || translated.trim().length === 0) {
    throw new Error('Empty translation result');
  }

  let cleanedTranslation = translated.trim();

  // Remove common preambles and introductions in multiple languages
  const preamblePatterns = [
    /^(claro|certainly|of course|bien sûr|seguro|sim|yes)[.,!?\s]*/i,
    /^(aqui está|here is|voici|hier ist)[^:]*:\s*/i,
    /^(here is the translation|voici la traduction|aquí tienes|aqui está)[^:]*:\s*/i,
    /^(i'll translate|je vais traduire|voy a traducir)[^:]*:\s*/i,
  ];

  for (const pattern of preamblePatterns) {
    cleanedTranslation = cleanedTranslation.replace(pattern, '');
  }

  // Remove markdown formatting that might have been added
  cleanedTranslation = cleanedTranslation
    .replace(/^#+\s+/gm, '') // Remove markdown headers (# ## ###)
    .replace(/^---+$/gm, '') // Remove horizontal rules
    .replace(/^\*+$/gm, ''); // Remove asterisk-only lines

  // Remove markdown emphasis markers if they're clearly wrong
  if (!cleanedTranslation.includes('*') || (cleanedTranslation.match(/\*/g) || []).length > 4) {
    cleanedTranslation = cleanedTranslation.replace(/\*\*/g, '').replace(/\*/g, '');
  }

  // Deterministically remove forbidden punctuation before final validation
  cleanedTranslation = sanitizeTranslationOutput(cleanedTranslation);

  if (!cleanedTranslation || cleanedTranslation.length === 0) {
    throw new Error('Translation became empty after cleanup');
  }

  // Check for forbidden dash characters after sanitization
  if (/[\u2013\u2014]/.test(cleanedTranslation)) {
    throw new Error('Translation contains forbidden em dash or en dash characters');
  }

  // Check for common error patterns that indicate AI responses instead of pure translations
  const errorPatterns = [
    /^(i apologize|je suis désolé|lo siento|desculpe)/i,
    /^(i cannot|je ne peux pas|no puedo|não posso)/i,
    /^(as an ai|comme une ia|como una ia|como uma ia)/i,
    /^(sorry|désolé|perdón|desculpa)/i,
    /^(here is|voici|aquí tienes|aqui está)/i,
  ];

  for (const pattern of errorPatterns) {
    if (pattern.test(cleanedTranslation.trim())) {
      throw new Error('Translation contains unwanted explanatory text');
    }
  }

  return true;
};

// Language-specific validation rules
export const LANGUAGE_SPECIFIC_VALIDATION = {
  'pt-PT': {
    // European Portuguese should use "tu" form
    informalMarkers: ['tu', 'teu', 'tua', 'teus', 'tuas', 'contigo'],
    formalMarkers: ['você', 'vocês', 'consigo']
  },
  'pt-BR': {
    // Brazilian Portuguese should use "você" form
    informalMarkers: ['você', 'seu', 'sua', 'seus', 'suas'],
    formalMarkers: ['vossa', 'vosso', 'vós']
  },
  'es-ES': {
    // Spanish should use "tú" form
    informalMarkers: ['tú', 'tu', 'tus', 'te', 'contigo'],
    formalMarkers: ['usted', 'ustedes', 'con usted']
  },
  'fr-FR': {
    // French should use "tu" form
    informalMarkers: ['tu', 'ton', 'ta', 'tes', 'toi'],
    formalMarkers: ['vous', 'votre', 'vos']
  },
  'de-DE': {
    // German should use "du" form
    informalMarkers: ['du', 'dein', 'deine', 'dir', 'dich'],
    formalMarkers: ['sie', 'ihnen', 'ihr', 'ihre']
  },
  // Backwards-compatible aliases, in case older code passes short language codes
  'es': {
    informalMarkers: ['tú', 'tu', 'tus', 'te', 'contigo'],
    formalMarkers: ['usted', 'ustedes', 'con usted']
  },
  'fr': {
    informalMarkers: ['tu', 'ton', 'ta', 'tes', 'toi'],
    formalMarkers: ['vous', 'votre', 'vos']
  },
  'de': {
    informalMarkers: ['du', 'dein', 'deine', 'dir', 'dich'],
    formalMarkers: ['sie', 'ihnen', 'ihr', 'ihre']
  }
};

// Function to validate language-specific requirements
export const validateLanguageSpecific = (translated, targetLanguage) => {
  const rules = LANGUAGE_SPECIFIC_VALIDATION[targetLanguage];
  if (!rules) return true;

  const normalized = translated.toLowerCase();

  const hasInformal = rules.informalMarkers.some(marker => {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(normalized);
  });

  const hasFormal = rules.formalMarkers.some(marker => {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(normalized);
  });

  if (hasFormal && !hasInformal) {
    console.warn(`Translation for ${targetLanguage} contains formal language, should be informal`);
  }

  return true;
};
